import { Outlet } from '@tanstack/react-router';
import { useAtom } from 'jotai';
import { useCallback, useEffect, useRef, type RefObject } from 'react';

import { sidebarStateAtom, type SidebarState } from '../../chat/atoms/sidebar-state-atom';

import { ChatSidebar } from '@/features/(insight)/chat/components/index';
import { useChatSidebarShortcut } from '@/features/(insight)/chat/hooks/index';
import { saveSidebarState } from '@/features/(insight)/chat/utils/index';

import { useRouteId } from '@/shared/hooks/use-route-id';
import { useSetInsightScrollContainer } from '../hook/use-insight-scroll-container';
import { InsightHeader } from './insight-header';

const HIDE_HEADER_ROUTE_IDS = [
  '/workspace/$workspaceId/insight/filtered-posts',
  '/workspace/$workspaceId/insight/filtered-profiles',
  '/workspace/$workspaceId/insight/post/$postId',
  '/workspace/$workspaceId/insight/referenced-posts',
];

export function InsightLayout() {
  const insightContainerRef = useRef<HTMLDivElement | null>(null);
  // Manage sidebar state at layout level for keyboard shortcut access
  const [sidebarState, setSidebarState] = useAtom(sidebarStateAtom);

  const routeId = useRouteId();
  const shouldHideHeader = HIDE_HEADER_ROUTE_IDS.includes(routeId);

  // Save sidebar state to localStorage whenever it changes
  useEffect(() => {
    saveSidebarState(sidebarState);
  }, [sidebarState]);

  const handleSidebarToggle = useCallback(() => {
    setSidebarState((prev) => ({
      ...prev,
      isOpen: !prev.isOpen,
    }));
  }, [setSidebarState]);

  const handleSidebarStateChange = useCallback(
    (newState: SidebarState) => {
      setSidebarState(newState);
    },
    [setSidebarState],
  );

  // Set up keyboard shortcut for sidebar toggle
  useChatSidebarShortcut(handleSidebarToggle);
  useSetInsightScrollContainer(insightContainerRef as RefObject<HTMLDivElement>);

  return (
    <div className="bg-background min-h-screen">
      <div className="mx-auto flex h-screen">
        {/* Main content area */}
        <div className="flex flex-1 flex-col overflow-y-auto" ref={insightContainerRef}>
          {!shouldHideHeader && <InsightHeader />}
          <Outlet />
        </div>

        {/* Chat sidebar */}
        <div className="hidden sm:block">
          <ChatSidebar
            sidebarState={sidebarState}
            onToggle={handleSidebarToggle}
            onSidebarStateChange={handleSidebarStateChange}
          />
        </div>
      </div>
    </div>
  );
}
